<template>
  <div
    ref="sourceTableContainer"
    class="report-sources__table"
  >
    <o-table
      v-show="data.length || loading"
      :backend-sorting="backendSorting"
      :class="{ 'report-sources__table-details-visible': detailsVisible }"
      :current-page.sync="currentPage"
      :data="data"
      default-sort="share"
      default-sort-direction="desc"
      detailed
      :has-detailed-visible="hasDetailedVisible"
      hoverable
      :loading="loading"
      :mobile-cards="false"
      :opened-detailed="openedDetailed"
      :paginated="showPagination"
      :per-page="perPage"
      :row-class="rowClass"
      :scrollable="!isPdf"
      @click="onRowClick"
      @sort="onSort"
      @update:openedDetailed="onOpenedDetailed"
    >
      <o-table-column
        field="funds.score"
        label="Risk Label"
        :sortable="!cpTable"
        :width="isPdf ? '120px' : '500px'"
      >
        <template #header="{ column }">
          <div class="flex align-center gap-1">
            <div>{{ column.label }} </div>
          </div>
        </template>
        <template #default="props">
          <div class="report-sources__table-label-wrapper">
            <div class="report-sources__table-risk">
              <GlRiskBadge
                v-if="props.row.funds && props.row.funds.name"
                :value="props.row.funds.score ?? -1"
              />
              <GlRiskBadge
                v-else-if="props.row.funds && props.row.funds.type"
                :value="props.row.funds.score ?? -1"
              />
              <GlRiskBadge
                v-else-if="props.row.typeData"
                :value="props.row.funds.score ?? -1"
              />
              <div v-else>
                –
              </div>
            </div>
            <div class="report-sources__table-owner-wrapper ellipsis">
              <div class="report-sources__table-owner">
                <div
                  v-if="props.row.emptyOwner"
                  class="ellipsis link"
                  @click="openInNewTabAddress(props.row.address)"
                >
                  <span v-if="!isPdf">
                    {{ trancateString(props.row.address) }}</span>
                  <div
                    v-if="isPdf"
                    class="ellipsis link"
                  >
                    <GlLink :url="openInNewTabAddressUrl(props.row.address)">
                      {{ trancateString(props.row.address) }}
                    </GlLink>
                  </div>
                </div>
                <div
                  v-else
                  class="ellipsis"
                  :class="{
                    'link-text max-content': (props.row.entityId || (props.row.entityData && props.row.entityData.id)) && !isPdf,
                  }"
                  @click="goToEntity(props.row.entityData ? props.row.entityData.id : props.row.entityId)"
                >
                  {{ capitalizeFirstLetter(props.row.entityData ? props.row.entityData.name : props.row.owner) }}
                </div>
              </div>
              <div class="report-sources__table-label ellipsis">
                {{ props.row.funds.name || props.row.funds.type || props.row.typeData?.name || '–' }}
              </div>
            </div>
          </div>
        </template>
      </o-table-column>

      <o-table-column
        field="amount"
        label="Amount"
        position="right"
        :sortable="selectedToken.address !== 'all' && !isPdf"
        width="100px"
      >
        <template #header="{ column }">
          <div class="flex align-center flex-end gap-1">
            <div>{{ column.label }} </div>
            <gl-icon
              v-if="column.sortable"
              :height="16"
              name="sort-up-down"
              style="min-width: 16px"
              :width="16"
            />
          </div>
        </template>
        <template #default="props">
          <div class="flex column gap-1">
            <div class="report-sources__table--amount">
              <span
                :key="props.row.formattedAmount"
                v-popover:tooltip.top="
                  `${toComaSeparate(String(props.row.formattedAmount))}`
                "
              >{{ toComaSeparate(String(props.row.formattedAmount)) }}
              </span>
              <GlSkeletonAmount type="amount" />
            </div>
            <div
              v-if="props.row.amountCur"
              class="report-sources__table--price"
            >
              <span
                :key="props.row.amountCur"
                v-popover:tooltip.top="
                  `${priceMessage('current', props.row.amountCurTimestamp)}`
                "
              >
                {{ formatByPrice(props.row.amountCur) }}
              </span>
              <GlSkeletonAmount type="price" />
            </div>
          </div>
        </template>
      </o-table-column>

      <o-table-column
        field="share"
        label="Exposure"
        position="right"
        sortable
        width="50px"
      >
        <template #header="{ column }">
          <div class="flex align-center flex-end gap-1">
            <div>{{ column.label }} </div>
            <gl-icon
              v-if="!isPdf"
              :height="16"
              name="sort-up-down"
              style="min-width: 16px"
              :width="16"
            />
          </div>
        </template>
        <template #default="props">
          <div class="flex column gap-1">
            <div class="report-sources__table--share">
              <span
                :key="props.row.share"
                v-popover:tooltip.top="`${props.row.share * 100}%`"
              >
                {{ formatShare(roundShare(props.row.share)) }}
              </span>
              <GlSkeletonAmount type="percent" />
            </div>
            <div v-if="isPdf">
&nbsp;
            </div>
          </div>
        </template>
      </o-table-column>

      <o-table-column
        field="directTx"
        label="Root Tx"
        position="left"
        :width="isPdf ? '60px' : '130'"
      >
        <template #header="{ column }">
          <div
            class="flex align-center gap-1 relative"
            :class="{ 'flex-end': isPdf }"
          >
            <div>{{ column.label }} </div>

            <GlPopoverHint
              v-if="!isPdf"
              v-model="hoveredTooltipRoot"
              :boundaries-element="sourceTableContainer"
              closable
              content-padding="24px"
              head-text="Root Tx"
            >
              <template #action-element>
                <gl-icon
                  class="pointer"
                  :height="16"
                  name="ICN - Info"
                  :width="16"
                />
              </template>
              <template #content-body>
                <div class="report-sources__table-popover-content-wrapper">
                  <div class="fs-12 mb-2">
                    "Root Tx" shows direct transactions of funds from wallet,
                    marking the point where funds first enter the wallet.
                  </div>
                  <div class="report-sources__table-popover-img-wrapper">
                    <img src="~@/assets/images/hints/rootTx-hint.png">
                  </div>
                </div>
              </template>
            </GlPopoverHint>
          </div>
        </template>
        <template #default="props">
          <div class="flex column gap-1">
            <div
              class="flex align-center"
              :class="{ 'flex-end': isPdf }"
            >
              <div
                v-if="!isPdf"
                v-popover:tooltip.top="'Open in new tab'"
                class="report-sources__table--root-tx soft-blue-text pointer bold-600"
                @click="openLinkInNewTab(Array.isArray(props.row.directTx) ? props.row.directTx[0] : props.row.directTx, coinType, 'tx')"
              >
                {{ trancateString(Array.isArray(props.row.directTx) ? props.row.directTx[0] : props.row.directTx, 4) || '--' }}
              </div>
              <gl-menu-item
                v-if="!isPdf"
                v-popover:tooltip.top="'Copy'"
                icon="copy-blue"
                :icon-height="20"
                :icon-width="20"
                @click="toClipBoard(Array.isArray(props.row.directTx) ? props.row.directTx[0] : props.row.directTx)"
              />
              <div
                v-if="isPdf"
                class="report-sources__table--root-tx soft-blue-text pointer bold-600"
              >
                <GlLink :url="openInNewTabUrl(Array.isArray(props.row.directTx) ? props.row.directTx[0] : props.row.directTx)">
                  {{ trancateString(Array.isArray(props.row.directTx) ? props.row.directTx[0] : props.row.directTx, 4) || '--' }}
                </GlLink>
              </div>
            </div>
            <div v-if="isPdf">
&nbsp;
            </div>
          </div>
        </template>
      </o-table-column>

      <o-table-column
        field="tx_hash"
        label="Leaf Tx"
        position="left"
        :width="isPdf ? '60px' : '130'"
      >
        <template #header="{ column }">
          <div
            class="flex align-center gap-1"
            :class="{ 'flex-end': isPdf }"
          >
            <div>{{ column.label }} </div>
            
            <GlPopoverHint
              v-if="!isPdf"
              v-model="hoveredTooltipLeaf"
              :boundaries-element="sourceTableContainer"
              closable
              content-padding="24px"
              head-text="Leaf Tx"
            >
              <template #action-element>
                <gl-icon
                  class="pointer"
                  :height="16"
                  name="ICN - Info"
                  :width="16"
                />
              </template>
              <template #content-body>
                <div class="report-sources__table-popover-content-wrapper">
                  <div class="fs-12 mb-2">
                    "Leaf Tx" identifies the last transaction before funds reach
                    the wallet, helping to trace the origin of the money.
                  </div>
                  <div class="report-sources__table-popover-img-wrapper">
                    <img src="~@/assets/images/hints/leafTx-hint.png">
                  </div>
                </div>
              </template>
            </GlPopoverHint>
          </div>
        </template>
        <template #default="props">
          <div class="flex column gap-1">
            <div
              v-if="checkMultiple(props.row.tx_hash)"
              class="report-sources__table--leaf-tx"
              :class="{ 'text-right mr-2': isPdf }"
            >
              Multiple
            </div>
            <div
              v-else
              class="flex align-center"
              :class="{ 'flex-end': isPdf }"
            >
              <div
                v-if="!isPdf"
                v-popover:tooltip.top="'Open in new tab'"
                class="report-sources__table--leaf-tx soft-blue-text pointer bold-600"
                @click="openLinkInNewTab(props.row.tx_hash, coinType, 'tx')"
              >
                {{ trancateString(props.row.tx_hash, 4) || '--' }}
              </div>
              <gl-menu-item
                v-if="!isPdf"
                v-popover:tooltip.top="'Copy'"
                class="sidebar__history-copy"
                icon="copy-blue"
                :icon-height="20"
                :icon-width="20"
                @click="toClipBoard(props.row.tx_hash)"
              />
              <div
                v-if="isPdf"
                class="report-sources__table--leaf-tx soft-blue-text pointer bold-600"
              >
                <GlLink :url="openInNewTabUrl(props.row.tx_hash)">
                  {{ trancateString(props.row.tx_hash, 4) || '--' }}
                </GlLink>
              </div>
            </div>
            <div v-if="isPdf">
&nbsp;
            </div>
          </div>
        </template>
      </o-table-column>

      <o-table-column
        field="depthSortValue"
        label="Depth"
        position="right"
        :sortable="!isPdf && !cpTable"
        :width="isPdf ? '40px' : '60px'"
      >
        <template #header="{ column }">
          <div class="flex align-center flex-end gap-1">
            <div>{{ column.label }} </div>
            <gl-icon
              v-if="column.sortable"
              :height="16"
              name="sort-up-down"
              style="min-width: 16px"
              :width="16"
            />
          </div>
        </template>
        <template #default="props">
          <div class="flex column gap-1">
            <div class="report-sources__table--depth">
              {{ findMinMaxFields(props.row.depth, false) }}
            </div>
            <div class="report-sources__table--depth-label fs-12 grey-text-6-e">
              {{ findMinMaxFieldsDirectionLabel(props.row.depth) }}
            </div>
          </div>
        </template>
      </o-table-column>

      <o-table-column
        field="directStamp"
        label="Date & Time"
        position="right"
        sortable
        width="60"
      >
        <template #header="{ column }">
          <div class="flex align-center flex-end gap-1">
            <div>{{ column.label }} </div>
            <gl-icon
              v-if="!isPdf"
              :height="16"
              name="sort-up-down"
              style="min-width: 16px"
              :width="16"
            />
          </div>
        </template>
        <template #default="props">
          <div
            class="report-sources__table-date"
            v-html="getMinTimestampDate(props.row.directStamp ?? props.row.timestamp)"
          />
        </template>
      </o-table-column>
      <template #detail="props">
        <div>
          <SwapData
            v-if="props.row.listType === 'Swap'"
            :currencies="currencies"
            :input="props.row.inputAmounts"
            :output="props.row.outputAmounts"
          />
        </div>
      </template>

      <template #empty>
        <div
          v-if="loading"
          class="flex align-center justify-center"
        >
          <gl-loader />
        </div>
        <div
          v-else
          class="empty-users-data flex column align-center"
        >
          <gl-icon
            :height="24"
            name="statistic"
            :width="24"
          />
          No data here yet
        </div>
      </template>
      <template
        v-if="showPagination"
        #pagination
      >
        <div class="pp-pagination-table">
          <div class="pp-pagination-table__left">
            <span
              v-if="!backendPagination"
            ><span class="pp-pagination-table__left-count">{{ data.length }}</span>
              {{
                pluralize({
                  count: data.length,
                  singular: 'Result',
                  plural: 'Results',
                })
              }}</span>
          </div>
          <div class="pp-pagination-table__right">
            <o-pagination
              v-if="showPagination"
              :current.sync="currentPage"
              :per-page="perPage"
              :range-after="0"
              :range-before="0"
              :total="total"
              @change="pageChange"
            >
              <template #default />
              <template #previous="props">
                <div class="pp-pagination-table__button-prev">
                  <o-pagination-button
                    class="pp-pagination-table__button-first-page"
                    :page="props.page"
                  >
                    <gl-menu-item
                      :disabled="props.page.disabled"
                      icon="double-arrow-left-2"
                      :icon-height="16"
                      :icon-width="16"
                      label=""
                      not-outline
                      @click.prevent="pageChange(1)"
                    />
                  </o-pagination-button>
                  <o-pagination-button :page="props.page">
                    <gl-menu-item
                      :disabled="props.page.disabled"
                      icon="arrow-left-2"
                      :icon-height="16"
                      :icon-width="16"
                      label=""
                      not-outline
                    />
                  </o-pagination-button>
                  <div class="pp-pagination-table__current">
                    {{ props.page.number + 1 }}
                  </div>
                  <div class="pp-pagination-table__current-total">
                    {{ `of ${totalPages}` }}
                  </div>
                </div>
              </template>
              <template #next="props">
                <div class="pp-pagination-table__button-next">
                  <o-pagination-button :page="props.page">
                    <gl-menu-item
                      :disabled="props.page.disabled"
                      icon="arrow-right-2"
                      :icon-height="16"
                      :icon-width="16"
                      label=""
                      not-outline
                    />
                  </o-pagination-button>
                  <o-pagination-button :page="props.page">
                    <gl-menu-item
                      :disabled="props.page.disabled"
                      icon="double-arrow-right-2"
                      :icon-height="16"
                      :icon-width="16"
                      label=""
                      not-outline
                      @click.prevent="pageChange(totalPages)"
                    />
                  </o-pagination-button>
                </div>
              </template>
            </o-pagination>
          </div>
        </div>
      </template>
    </o-table>
   
    <div
      v-if="!data.length && !loading"
      class="report-sources__table-no-data"
    >
      <gl-icon
        :height="24"
        name="statistic"
        :width="24"
      />
      No data here yet
    </div>
  </div>
</template>

<script>
// Components
import GlLoader from '@/components/gl-loader'
import GlIcon from '@/components/gl-icon'
import GlBadgeInfo from '@/components/gl-badge-info'
import SwapData from '@/pages/report/components/gl-report/table/SwapData'
import GlMenuItem from '@/components/gl-menu-item.vue'
import GlPopoverHint from '@/components/gl-popover-hint.vue'
import GlLink from '@/components/gl-link'
import GlRiskBadge from '@/components/risk/gl-risk-badge'
import GlSkeletonAmount from '@//components/skeleton/gl-skeleton-amount'
// Utils
import { formatDate } from '@/utils/format-date'
import { formatBtcAmount } from '@/utils/format-btc-amount'
import { capitalizeFirstLetter, trancateString, pluralize } from '@/utils/text-formatter'
import { findMinMaxFields, findMinMaxFieldsDirectionLabel } from '@/utils/model'
import { formatShare, roundShare } from '@/utils/sourcesFormatter'
import { checkMultiple } from '@/utils/report-data-formatter'
import { toComaSeparate } from '@/utils/formatNumber'
import { formatByPrice, priceMessage } from '@/utils/format-by-price'
import { goToEntity } from '@/utils/go-to-route'
import { toClipBoard } from '@/utils/helpers'
import { linkDataFormatter, windowOpenLink } from '@/utils/go-to-route'

// Vuex
import { mapState } from 'vuex'

export default {
  components: {
    SwapData,
    GlMenuItem,
    GlLoader,
    GlIcon,
    GlBadgeInfo,
    GlPopoverHint,
    GlLink,
    GlRiskBadge,
    GlSkeletonAmount,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currencies: {
      type: Object,
      default: () => ({}),
    },
    selectedToken: {
      type: Object,
      default: () => ({}),
    },
    essential: {
      type: Boolean,
      default: false,
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    extraPdfParams: {
      type: Object,
      default: () => ({}),
    },
    backendPagination: {
      type: Boolean,
      default: false,
    },
    backendPerPage: {
      type: Number,
      default: 8,
    },
    currentBackendPage: {
      type: Number,
      default: 1,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    totalLoaded: {
      type: Number,
      default: 0,
    },
    backendSorting: {
      type: Boolean,
      default: false,
    },
    cpTable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hoveredTooltipRoot: false,
      hoveredTooltipLeaf: false,
      sourceTableContainer: null,
      perPageLocal: 8,
      currentPageLocal: 1,
      openedDetailed: [],
      detailsVisible: true,
      currentSortField: 'amount',
      currentSortOrder: -1,
    }
  },
  computed: {
    ...mapState('analytics', ['coinData', 'coinType']),
    total() {
      if (this.backendPagination) {
        return this.hasMore ? this.totalLoaded + this.perPage : this.totalLoaded
      }

      return this.data.length
    },
    totalPages() {
      if (this.backendPagination) {
        const total = Math.ceil(this.totalLoaded / this.perPage)
        return this.hasMore ? total + 1 : total
      }
      return Math.ceil(this.data.length / this.perPage)
    },
    showPagination() {
      if (this.backendPagination) {
        return true
      }
      return !!this.data.length && !this.isPdf
    },
    currentPage: {
      get() {
        if (this.backendPagination) {
          return this.currentBackendPage
        }
        return this.currentPageLocal
      },
      set(value) {
        this.currentPageLocal = value
      },
    },
    perPage: {
      get() {
        if (this.backendPagination) {
          return this.backendPerPage
        }
        return this.perPageLocal
      },
      set(value) {
        this.perPageLocal = value
      },
    },
  },
  watch: {
    data: {
      handler() {
        if (this.backendPagination) {
          return
        }
        this.pageChange(1)

        if (this.isPdf) {
          this.openedDetailed = [...this.data]
        }
      },
    },
  },
  mounted() {
    this.sourceTableContainer = this.$refs.sourceTableContainer
    if (this.isPdf) {
      this.openedDetailed = [...this.data]
    }
  },
  methods: {
    formatDate,
    toClipBoard,
    formatBtcAmount,
    capitalizeFirstLetter,
    findMinMaxFields,
    findMinMaxFieldsDirectionLabel,
    trancateString,
    roundShare,
    checkMultiple,
    formatShare,
    toComaSeparate,
    formatByPrice,
    priceMessage,
    goToEntity,
    linkDataFormatter,
    windowOpenLink,
    pluralize,
    pageChange(event) {
      if (this.backendPagination) {
        this.$emit('update:pageChange', event)
        return
      }

      this.currentPage = event
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      const currentPageRows = this.data.slice(start, end)
      this.detailsVisible = currentPageRows.some(row =>
        this.hasDetailedVisible(row),
      )
    },
    onSort(field) {
      if (this.cpTable) {
        if (field == 'share') field = 'amount'
      }
      if (this.currentSortField === field) {
        this.currentSortOrder = this.currentSortOrder === 1 ? -1 : 1
      } else {
        this.currentSortField = field
        this.currentSortOrder = 1
      }

      this.$emit('onSort', {
        sortField: this.currentSortField,
        sortOrder: this.currentSortOrder,
      })
    },
    getMinTimestampDate(val) {
      const timestamp = (val && val?.minimum) || val

      return timestamp
        ? this.formatDate(timestamp * 1000, 'dd.MM.yyyy HH:mm', true)
        : '--'
    },
    openLinkInNewTab(value, coin, type) {
      this.windowOpenLink(this.linkDataFormatter(value, coin, type)?.link)
    },
    openInNewTab(val) {
      const { href } = this.$router.resolve({
        name: 'analytics',
        query: { tx: val, type: this.coinType, essential: this.coinData.essential },
      })
      window.open(href, '_blank')
    },
    openInNewTabAddress(address) {
      const { href } = this.$router.resolve({
        name: 'analytics',
        query: { address, type: this.coinType, essential: this.coinData.essential },
      })
      window.open(href, '_blank')
    },
    openInNewTabUrl(val) {
      const baseUrl = `${this.extraPdfParams.feURL}analytics`
      const params = new URLSearchParams({
        tx: val,
        type: this.coinType,
        essential: this.coinData.essential
      })
      return `${baseUrl}?${params.toString()}`
    },
    openInNewTabAddressUrl(address) {
      const baseUrl = `${this.extraPdfParams.feURL}analytics`
      const params = new URLSearchParams({
        address,
        type: this.coinType,
        essential: this.coinData.essential
      })
      return `${baseUrl}?${params.toString()}`
    },
    hasDetailedVisible(row) {
      return row.inputAmounts && row.outputAmounts && row.listType === 'Swap'
    },
    rowClass(row) {
      return this.hasDetailedVisible(row) ? 'pointer' : ''
    },
    onRowClick(row) {
      const index = this.openedDetailed.indexOf(row)
      if (index !== -1) {
        this.openedDetailed.splice(index, 1)
      } else {
        this.openedDetailed.push(row)
      }
    },
    onOpenedDetailed(row) {
      this.openedDetailed = row
    },
  },
}
</script>
<style>
.report-sources__table {
  position: relative;
}
.report-sources__table-date {
  min-width: max-content;
  text-align: right;
}
.report-sources__table-date .pp-date__time {
  margin-top: 4px;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px !important;
  color: var(--dark-grey-6-e) !important;
}

.report-sources__table .o-table .o-table__th {
  color: var(--space-cadet) !important;
  font-size: 12px;
  padding: 8px 12px !important;
  padding-top: 10px !important;
  font-weight: 600 !important;
  text-transform: none;
  border: none;
  white-space: nowrap;
}
.report-sources__table .o-table .o-table__th.o-table__th-current-sort {
  font-weight: 900 !important;
}

.report-sources__table .o-table .o-table__td {
  font-size: 14px;
  padding: 8px 14px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border: none;
  min-width: max-content;
  border-bottom: 1px solid var(--Accent-Coton-Grey);
}

.report-sources__table .o-table__detail > td {
  padding: 0 8px 0 12px;
}

.report-sources__table tbody > tr .o-table__td.o-table__td-chevron {
  display: none;
}
.report-sources__table thead .o-table__th--detailed {
  display: none;
}

.report-sources__table-label-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 300px;
  max-width: 400px;
}

.report-sources__table-risk {
  display: flex;
  align-items: center;
  justify-content: center;
}

.report-sources__table-owner {
  color: var(--Basic-900, #091540);
  font-size: var(--Size-L, 16px);
  font-weight: 600;
  line-height: var(--Line-Height-XL, 24px);  
  letter-spacing: var(--Letter-Spacing-XS, -0.5px);
}

.report-sources__table-label {
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
  font-size: var(--Size-S, 12px);
  font-weight: 600;
  line-height: var(--Line-Height-S, 16px); 
}

.report-sources__table--amount {
  font-size: var(--Size-M, 14px);
  font-weight: 600;
  line-height: var(--Line-Height-L, 20px);  
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}
.report-sources__table--price {
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
  font-size: var(--Size-S, 12px);
  font-weight: 500;
  line-height: var(--Line-Height-S, 16px);  
}

.report-sources__table--share {
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px); 
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.report-sources__table
  .report-sources__table-details-visible
  tbody
  > tr
  .o-table__td.o-table__td-chevron {
  display: table-cell;
  padding: 0 4px !important;
  width: 0 !important;
}
.report-sources__table
  .report-sources__table-details-visible
  thead
  .o-table__th--detailed {
  display: table-cell;
  padding: 0 !important;
  padding-left: 8px !important;
  width: 0 !important;
}

.report-sources__table
  .report-sources__table-details-visible
  .o-table__detail
  > td {
  padding: 0 12px 0 8px;
}

.report-sources__table
  .report-sources__table-details-visible
  .o-table__th:nth-child(2),
.report-sources__table
  .report-sources__table-details-visible
  .o-table__td:nth-child(2) {
  padding: 0px !important;
}

.report-sources__table
  tbody
  > tr:has(+ .o-table__detail)
  .o-table__td-chevron
  .o-icon--clickable {
  transform: rotate(-90deg) !important;
}
.report-sources__table
  tbody
  > tr:has(+ .o-table__detail)
  .o-table__td-chevron
  .o-icon--clickable
  .mdi-chevron-right::before {
  color: var(--soft-blue);
}

.report-sources__table .o-table__td.o-table__td-chevron .o-icon--clickable {
  transform: rotate(90deg) !important;
}
.report-sources__table
  .o-table__td.o-table__td-chevron
  .o-icon--clickable
  .mdi-chevron-right::before {
  color: var(--space-cadet);
}

.report-sources__table tbody:before {
  line-height: 8px;
  content: '\200C';
  display: block;
}

.report-sources__table .o-table__th:last-child,
.report-sources__table .o-table__td:last-child {
  padding-right: 40px !important;
}

.report-sources__table .pp-pagination-table {
  padding: 16px;
  padding-top: 12px;
}

.report-sources__table-popover-content-wrapper {
  width: 470px;
  max-width: 70vw;
}

.report-sources__table-popover-img-wrapper img {
  object-fit: contain;
  height: 100%;
  width: 100%;
}

.report-sources__table .pp-pagination-table__button-first-page {
  margin-right: -4px;
}
.report-sources__table .pp-pagination-table .o-pag__link {
  border-color: var(--space-cade) !important;
}

.soft-blue-text {
  color: var(--reflex-bluet);
}

.report-sources__table .pp-pagination-table__left {
  color: var(--Basic-800, rgba(9, 21, 64, 0.68));
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);  
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.report-sources__table-no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 200px auto;
}

.report-sources__table .gl-popover-hint .trigger {
  display: flex !important;
  align-items: center;
}
</style>
